import cssBlankPseudoInit from "css-blank-pseudo/browser";
import NecIcon from "../../shared/necIcon";
import LicenseWizard from "./licenseWizard";
import { hideSpinner, showSpinner } from "./utils";
import { AuthType, FormDataKey } from "./types";
import { LoginForm, ResetPasswordForm, SuccessForm, authProviderIdAttribute } from "./forms.js";
import ForceDisconnectingPopup from "../mainWindow/forceDisconnectingPopup";
import LoginAdvancedError from "./advancedError";
import { ensureHtmlElement } from "@nectari/utils/html";

cssBlankPseudoInit();

/** @type {AuthType} */
const defaultAuthTypeIfBothAvailable = AuthType.external;
const loginForm = LoginForm.create();
const resetPasswordForm = ResetPasswordForm.create();
const successForm = SuccessForm.create();

function getActiveForm() {
    const activeForm = [loginForm, resetPasswordForm].find(form => form.isActive);

    if (!activeForm) {
        throw new Error("No active form");
    }

    return activeForm;
}

function getInactiveForm() {
    const inactiveForm = [loginForm, resetPasswordForm].find(form => !form.isActive);

    if (!inactiveForm) {
        throw new Error("No inactive form");
    }

    return inactiveForm;
}

function showAjaxSpinner() {
    showSpinner(getActiveForm().card);
}

function hideAjaxSpinner() {
    [loginForm, resetPasswordForm].forEach(form => hideSpinner(form.card));
}

// Override default loading spinner logic from MasterLayout.cshtml
window.showAjaxSpinner = showAjaxSpinner;
window.hideAjaxSpinner = hideAjaxSpinner;

BINamespace.loginViewModel = function () {
    "use strict";
    var self = this;

    /**
     * Enabled Auth providers for the selected CentralPoint.
     */
    self._authServices = [];

    /**
     * Indiactes whether or not Basic Login is enabled for the selected CentralPoint.
     */
    self._isEnabledBasicLogin = false;

    /**
     * Signalr connection id used to send and receive specific messages
     */
    self.signalRId = null;

    /**
     * This is a copy of the SuccessfulLogin in AccountController used only in case of iFrame for now
     * @param {{ ReturnUrl: string | URL; CP: string; Lang: string; M: string; }} actionModel
     */
    self._successfulLogin = function (actionModel) {
        if (actionModel) {
            var returnUrl = !actionModel.ReturnUrl
                ? new URL("MainInterface/MainWindow", window.location.origin)
                : new URL(actionModel.ReturnUrl, window.location.origin);

            if (actionModel.CP && !returnUrl.searchParams.get("CP")) {
                returnUrl.searchParams.append("CP", actionModel.CP);
            }

            if (actionModel.Lang && !returnUrl.searchParams.get("Lang")) {
                returnUrl.searchParams.append("Lang", actionModel.Lang);
            }

            if (actionModel.M && !returnUrl.searchParams.get("M")) {
                returnUrl.searchParams.append("M", actionModel.M);
            }

            // We redirect
            window.location.replace(returnUrl);
        }
    };

    /**
     * @param {boolean} isRedirecting 
     */
    self._setRedirecting = function (isRedirecting) {
        loginForm.element.classList.toggle("redirecting", isRedirecting);

        if (isRedirecting) {
            showSpinner(loginForm.container);
        } else {
            hideSpinner(loginForm.container);
        }
    }

    self._loginResultHandler = async function (result, isSignalR = false) {
        if (result.success) {
            BINamespace.SignalRSyncManager.send("authenticated");
            if (Modernizr.localstorage) {
                var storage = window.localStorage;
                // login successful, save or delete credentials in local storage depending on rememberMe value
                BINamespace.clearLocalStorageCredentials();
                if (result.rememberMe) {
                    if (result.isOAuth) {
                        storage.setItem("CompOAuthProviderId", result.providerId);
                    }
                    else {
                        storage.setItem("CompUsername", result.username);
                        storage.setItem("CompPassword", result.password);
                        storage.setItem("CompDomain", result.domain);
                    }
                    storage.setItem("CompCentralPoint", result.centralPoint);
                }
                storage.setItem("language", result.language);
                storage.setItem("forceMobileMode", result.forceMobileMode);
            }

            loginForm.displayLoaders();
            // proceed past the login page by submitting second form
            // show loading message
            successForm.setCentralpoint(result.centralPoint);
            successForm.setLanguage(result.language);
            if (result.licenseOfflineExpiration) {
                alert(result.error);
            }
            if (result.needsLicenseKey) {
                self.hKey = result.hardwareKey || "";
                await self._showLicensePopup(result.error, result.licenseStatus).finally(() => loginForm.hideLoaders());
            }
            else if (result.hasToUpdate) {
                if (await self.updateFeatures()) {
                    if (result.isEvictionNeeded) {
                        self._showForceDisconnectingPopup(result, isSignalR);
                    }
                    else {
                        self._setRedirecting(true);
                        successForm.element.submit();
                    }

                    // Early return to avoid removing the redirection overlay if it exists.
                    // If it exists, the overlay should persist until navigation to the mainwindow is complete.
                    return;
                }
            }
            else if (result.isEvictionNeeded) {
                self._showForceDisconnectingPopup(result, isSignalR);
                return;
            }
            else {
                self._succesLoginResultHandler(result, isSignalR);
                return;
            }
        }

        loginForm.toggleSubmitButton(true);

        if (result.needsLicenseKey) {
            self.hKey = result.hardwareKey || "";
            self._showLicensePopup(result.error, result.licenseStatus).finally(() => loginForm.hideLoaders());
        }
        else {
            loginForm.toggleSubmitButton(true);
            // login failed, display error message            
            if (result.error) {
                self.addLoginError(result.error);
            }
            else if (result.Errors) {
                self.addLoginError(result.Errors.error?.errors?.[0], result.Errors?.traceparent);
                console.log(result.Errors.advancedError?.errors?.[0]);
            }
        }
    };

    self._showForceDisconnectingPopup = function (result, isSignalR) {
        new ForceDisconnectingPopup(
            result.browserTabId,
            () => self._succesLoginResultHandler(result, isSignalR),
            () => self._onCancelCallBack(),
            (errorMsg, error) => self._onErrorCallBack(errorMsg, error)
        ).open();
    };

    self._succesLoginResultHandler = function (result, isSignalR = false) {
        loginForm.displayLoaders();
        // When we do ajax call with that handler, the second value has data which is neither true or false.
        // We want to make sure that the code is passing here only when isSignalR is really the true value itself. (When IFrame is used, see line 545)
        if (isSignalR === true) {
            var actionModel = {
                CP: result.centralPoint,
                Lang: result.language,
                ReturnUrl: successForm.returnUrl.value,
                M: result.forceMobileMode
            };
            self._successfulLogin(actionModel);
        }
        else {
            self._setRedirecting(true);
            successForm.element.submit();
        }
    };

    self._onCancelCallBack = function () {
        loginForm.hideLoaders();
        loginForm.toggleSubmitButton(true);
    };

    self._onErrorCallBack = function (errorMsg, error) {
        console.error("Error:", error);
        self.addLoginError(errorMsg);
        loginForm.toggleSubmitButton(true);
    };

    self._showLicensePopup = async function (error, licenseStatus) {
        await new LicenseWizard(
            licenseStatus,
            self._loginFormSubmitter.bind(self),
            self._loginResultHandler.bind(self),
            self._showLicensePopupReason.bind(self, error),
            self.addLoginError
        ).initialize();
    };

    /**
     * Provide a message in license key window to highlight the reason why the popup is shown
     * @param {string} message
     */
    self._showLicensePopupReason = function (message) {
        // Check if message is undefined or null before appending it in the div
        if (message) {
            var $reasonDiv = $(".licenseLogo");
            if ($reasonDiv.length) {
                $reasonDiv.empty()
                    .append("<p>" + message + "</p>");
            }
        }
    };

    /**
     * @param {SubmitEvent} [event]
     */
    self._loginFormSubmitter = async function (event) {
        event?.preventDefault();
        loginForm.clearErrorMessage();
        loginForm.toggleSubmitButton(false);

        const body = loginForm.getFormData();
        const forceMobileMode = body.get(FormDataKey.screenType) === "mobile"
            ? "true"
            : "false";

        body.set(FormDataKey.ForceMobileMode, forceMobileMode);
        body.delete(FormDataKey.screenType);

        try {
            loginForm.displayLoaders();
            const response = await fetch(loginForm.element.action, { method: loginForm.element.method, body });
            const json = await response.json();
            self._loginResultHandler(json);
        }
        catch {
            loginForm.hideLoaders();
        }
        finally {
            return false;
        }
    };

    //#region Reset Password
    /**
     * @param {SubmitEvent} event
     */
    self._onSubmitResetPassword = async function _onSubmitResetPassword(event) {
        event.preventDefault();

        if (!resetPasswordForm.element.checkValidity()) {
            return;
        }

        resetPasswordForm.clearErrorMessage();
        resetPasswordForm.toggleSubmitButton(false);

        const body = resetPasswordForm.getFormData();

        body.set("centralPointHash", body.get("centralpointResetPassword") ?? "");

        try {
            resetPasswordForm.displayLoaders();

            const response = await fetch(
                resetPasswordForm.element.action,
                {
                    method: resetPasswordForm.element.method,
                    body
                });

            if (!response.ok) {
                return;
            }

            const json = await response.json();

            if (json.Errors && json.Errors?.error?.errors?.[0]) {
                const error = json.Errors.error.errors[0];
                resetPasswordForm.showErrorMessage(error);
            }
            else {
                $("#resetPasswordForm .formFields").hide();
                $("#resetPasswordForm .formActions input").hide();
                $("#resetPasswordForm #resetPasswordSuccess").show();
            }

            resetPasswordForm.toggleSubmitButton(true);
        }
        finally {
            resetPasswordForm.hideLoaders();
        }
    };
    //#endregion Reset Password

    //#region Update Feature
    self.updateFeatures = async function () {
        const url = BINamespace.getPathUrl() + "/Account/ExecutePreLoginFeatures/" + BINamespace.BrowserTabId;
        loginForm.toggleSubmitButton(false);

        const abortController = new AbortController();

        /**
         * Add the upgrade messages in the login form.
         * @type {(message: string | undefined) => void} 
         */
        const showUpgradeMessage = (message) => loginForm.displayLoaders(message, "updatingLoader");

        BINamespace.SignalRSyncManager.receive("CentralPointUpgradeProgress", (message) => {
            showUpgradeMessage(message);
        }, { signal: abortController.signal });

        // Show a generic upgrade message in case we don't receive any messages from SignalR, e.g. when SignalR isn't connected (yet).
        // @ts-expect-error test
        showUpgradeMessage(BINamespace.loginLabels.CENTRAL_POINT_UPGRADE_GENERIC_MESSAGE);

        const response = await fetch(url, {
            method: "POST",
            headers: {
                contentType: "application/json; charset=utf-8"
            },
            cache: "no-cache",

        });
        abortController.abort();

        if (!response.ok) {
            throw new Error(response.statusText);
        }

        const json = await response.json();

        if (!json.success) {
            const error = json.error ?? json.Errors?.advancedError?.errors?.[0];
            const errId = json.error ?? json.Errors?.traceparent;
            if (error) {
                await new LoginAdvancedError(BINamespace.BrowserTabId, error, errId.errors?.[0]).initialize();
            }

            loginForm.toggleSubmitButton(true);
            self.addLoginError(BINamespace.loginLabels.MSG_CP_UPDATE_ERROR, errId);

            return false;
        }
        else if (json.needLicenseActivation) {
            var licenseMsg = BINamespace.loginLabels.STR_LICENSE_NEEDS_REEACTIVATION_DUE_TO_UPGRADE;
            if (json.hasLicensePermission) {
                // The license needs to be reactivated after the update scripts, such as after the bilicense upgrade
                self._showLicensePopup(licenseMsg, "LicenseNotActivated").finally(() => loginForm.hideLoaders());;
            }
            else {
                // Only users that have access to license registration can see the license popup
                // Otherwise, they will receive an error message.
                loginForm.toggleSubmitButton(true);
                self.addLoginError(licenseMsg);
            }
            return false;
        }

        return true;
    };
    //#endregion Update Feature

    self.closeApp = function () {
        window.close();
    };

    self.onChangeCentralPoint = function () {
        self.clearLoginOptionsError();
        self.onSetCentralPoint();
    };

    self.onSetCentralPoint = function () {
        /* Called on every central point change */
        // We load the auth services
        self.loadAuthServices();
    };

    self.loadAuthServices = async function loadAuthServices() {
        loginForm.displayLoaders();
        loginForm.setAuthAttributes(AuthType.none, AuthType.none);

        const centralPoint = loginForm.centralpointInput.value;

        if (!centralPoint) {
            const errorMessage = BINamespace.loginLabels.STR_NO_CP;
            console.error(errorMessage);
            self.addLoginError(errorMessage);
            return;
        }

        const controller = new AbortController();

        /**
         * @param {KeyboardEvent} event 
         */
        function onEscapePressed(event) {
            if (event.key === "Escape") {
                controller.abort();
            }
        }

        document.addEventListener("keyup", onEscapePressed);

        const url = new URL(BINamespace.getPathUrl() + "/Auth/GetLoginSettings");
        url.searchParams.append("centralPointHash", centralPoint);

        const showAdmin = new URLSearchParams(window.location.search).get('ShowAdmin');

        if (showAdmin !== null) {
            url.searchParams.append("ShowAdmin", showAdmin);
        }

        try {
            const response = await fetch(url, {
                cache: "no-cache",
                headers: {
                    "Content-Type": "application/json; charset=utf-8"
                },
                signal: controller.signal
            });

            if (!response.ok) {
                throw new Error(response.statusText);
            }

            const data = await response.json();

            self.processLoginSettings(data);
        }
        catch (error) {
            if (error instanceof DOMException && error.name === "AbortError") {
                console.warn("Loading aborted.");
                self.setLoginOptionsError();
            }
            else if (error instanceof Error) {
                console.error("GetLoginSettings failed:", error);
                self.setLoginOptionsError(error.message);
            }
            else {
                console.error("GetLoginSettings failed:", error);
                self.setLoginOptionsError(error);
            }

            loginForm.setAuthAttributes(AuthType.none, AuthType.none);
        }
        finally {
            document.removeEventListener("keyup", onEscapePressed);
            loginForm.hideLoaders();
        }
    };

    /**
     * @param {*} data
     */
    self.processLoginSettings = function processLoginSettings(data) {
        if (!data) {
            self.setLoginOptionsError();
            return;
        }

        if (data.Errors) {
            const errorMessage = data.Errors?.error?.errors?.[0] ?? ""; // ?????
            console.error("Central point not found:", errorMessage);
            self.setLoginOptionsError(errorMessage);
            return;
        }

        /** @type {import("./types").AuthService[]} */
        const authProviders = data.authProviders;
        const externalLogin = authProviders?.length > 0;
        const basicLogin = !!data.isEnabledBasicLogin;

        const authType = externalLogin && basicLogin
            ? AuthType.both
            : externalLogin
                ? AuthType.external
                : basicLogin
                    ? AuthType.basic
                    : AuthType.none;

        const currentAuthType = authType === AuthType.both
            ? defaultAuthTypeIfBothAvailable
            : authType;

        loginForm.setAuthAttributes(authType, currentAuthType);

        if (authType !== AuthType.basic) {
            loginForm.setAuthProviders(authProviders);
        }
    }

    /**
     * Sets the error state in the UI and shows the reload-login-options button.
     * @param {string} [error]
     */
    self.setLoginOptionsError = function (error) {
        error = error || "";
        self.addLoginError(error);
        loginForm.toggleServerError(true);
    };

    self.authInitFlow = function (providerId, cp, lang, forceMobileMode, isAuto) {
        var returnUrl = encodeURIComponent(successForm.returnUrl.value);
        var isIframe = BINamespace.inIframe();
        var url =
            `${BINamespace.getPathUrl()}/Auth/InitAuthFlow?serviceId=${providerId}&&centralPointHash=${cp}&&returnUrl=${returnUrl}&&lang=${lang}&&isIframe=${isIframe}&&signalRId=${self.signalRId}&&forceMobileMode=${forceMobileMode}`;

        if (isIframe) {
            loginForm.displayLoaders();
            window.open(`${url}&&promptHidden=${isAuto}`);
        }
        else {
            window.location.href = `${url}&&promptHidden=${isAuto}`;
        }
    };

    self.authCleanStorage = function () {
        if (Modernizr.localstorage) {
            var storage = window.localStorage;
            storage.removeItem("CompOAuthProviderId");
        }
    };
    //#endregion

    /**
     * Displays the error message on the login form.
     * @param {string} message The error message to display.
     * @param {ErrorId} [errorId] Object containing the error id.
     */
    self.addLoginError = function (message, errorId) {
        self.authCleanStorage();
        // We hide the loaders so the user can take back the control
        loginForm.hideLoaders();

        if (message) {
            let errId = errorId?.errors?.[0] ?? "";

            if (errId) {
                errId = errId.split("-")[1];
                message = `${message} ${BINamespace.loginLabels.STR_ERROR_ID} ${errId}`
            }

            loginForm.showErrorMessage(BINamespace.HTMLDecode(message));
        }
    };

    /**
     * Clears the options error state in the UI.
     */
    self.clearLoginOptionsError = function () {
        loginForm.clearErrorMessage();
        loginForm.toggleServerError(false);
    };

    self.signalRCallBackGetConnectionId = function (id) {
        self.signalRId = id;
        // The flow should be initialized after signalR
        if (Modernizr.localstorage) {
            var storage = window.localStorage;
            if (storage.getItem("CompOAuthProviderId") &&
                storage.getItem("CompCentralPoint") &&
                storage.getItem("language") &&
                storage.getItem("forceMobileMode")) {
                self.authInitFlow(storage.getItem("CompOAuthProviderId"), storage.getItem("CompCentralPoint"), storage.getItem("language"), storage.getItem("forceMobileMode"), true);
            }
        }
    };

    self.signalRCallBackConnectIframe = function (authData) {
        if (authData) {
            BINamespace.authData = authData;
            loginForm.clearErrorMessage();
            self._loginResultHandler(BINamespace.parseJsonString(authData), true);
        }
    };

    self.initSignalR = function () {
        BINamespace.SignalRSyncManager.receive("ConnectIframe", self.signalRCallBackConnectIframe.bind(this));
        BINamespace.SignalRSyncManager.receive("ErrorIframe", self.addLoginError.bind(this));
        BINamespace.SignalRSyncManager.receive("GetConnectionId", self.signalRCallBackGetConnectionId.bind(this));
        BINamespace.SignalRSyncManager.send("getConnectionId");
    };

    self.loginTemplateContainsErrors = function () {
        const errorMessage = loginForm.errorMessage;
        const hasError = errorMessage.innerHTML.length > 0;

        // Shouldn't be done here but that's life
        errorMessage.style.visibility = hasError ? "visible" : "hidden";

        return hasError;
    };

    /**
     * @param {Event} event
     */
    self._onToggleScreenType = function (event) {
        const target = event.target;

        if (!(target instanceof HTMLInputElement)) {
            return;
        }

        const forceMobileMode = target.value === "mobile" || BINamespace.isMobileBrowser();
        successForm.setMobileMode(forceMobileMode);
    };

    /**
     * @param {MouseEvent} event 
     * @this HTMLAnchorElement
     */
    self.onToggleAuthType = function onToggleAuthType(event) {
        event.preventDefault();
        loginForm.toggleAuthType();
    }

    /**
     * @param {Event} [event]
     * @this HTMLFormElement
     */
    self.onToggleForm = function onToggleForm(event) {
        const activeForm = getActiveForm();
        const targetForm = getInactiveForm();

        const activeFormData = new FormData(activeForm.element);

        const centralPointHash = activeFormData.has("CentralPointHash")
            ? activeFormData.get("CentralPointHash")
            : activeFormData.get("centralpointResetPassword");

        if (typeof centralPointHash === "string") {
            $(targetForm.centralpointSelect).data("kendoDropDownList")?.value(centralPointHash);
        }

        const domain = activeFormData.get("domain");

        if (typeof domain === "string") {
            targetForm.domainInput.value = domain;
        }

        const userName = activeFormData.get("username");

        if (typeof userName === "string") {
            targetForm.usernameInput.value = userName;
        }

        $("#resetPasswordForm .formFields").show();
        $("#resetPasswordForm .formActions input").show();
        $("#resetPasswordForm #resetPasswordSuccess").hide();

        activeForm.toggleActive();
        targetForm.toggleActive();

        event?.preventDefault();
    }

    /**
     * @param {MouseEvent} event 
     * @this HTMLDivElement
     */
    self.onClickAuthProvider = function onClickAuthProvider(event) {
        if (!(event.target instanceof HTMLButtonElement)) {
            return;
        }

        const providerId = event.target.getAttribute(authProviderIdAttribute);

        if (!providerId) {
            throw new Error("Provider id cannot be empty");
        }

        const formData = loginForm.getFormData();
        const centralPointHash = formData.get(FormDataKey.centralPointHash);
        const language = formData.get(FormDataKey.language);
        const screenType = formData.get(FormDataKey.screenType);

        const forceMobileMode = screenType === "mobile";

        self.authInitFlow(providerId, centralPointHash, language, forceMobileMode, false);
    }

    /**
     * @param {MouseEvent} event 
     * @this HTMLButtonElement
     */
    self.onReloadLoginOptions = function onReloadLoginOptions(event) {
        event.preventDefault();
        self.clearLoginOptionsError();
        self.loadAuthServices();
    };

    self.addListeners = function addListeners() {
        loginForm.usernameInput.addEventListener(
            "input",
            /**
            * @this HTMLInputElement
            */
            function myEvent() {
                this.value = this.value.toUpperCase();
            }
        );

        loginForm.authTypeToggles.forEach(element => {
            ensureHtmlElement(element, HTMLAnchorElement).addEventListener("click", self.onToggleAuthType);
        });

        loginForm.externalProviders.addEventListener("click", self.onClickAuthProvider);

        loginForm.formToggle.addEventListener("click", self.onToggleForm);
        resetPasswordForm.formToggle.addEventListener("click", self.onToggleForm);

        loginForm.element.addEventListener("submit", self._loginFormSubmitter);
        resetPasswordForm.element.addEventListener("submit", self._onSubmitResetPassword);

        loginForm.screenTypeToggle.addEventListener("change", self._onToggleScreenType);
        loginForm.reloadLoginOptions.addEventListener("click", self.onReloadLoginOptions);

        loginForm.closeAppButton?.addEventListener("click", self.closeApp);
    };

    self.initialize = function () {
        self.addListeners();

        const isCloud = $("#version").data("iscloud");
        if (isCloud) {
            let clickVersionCount = 0;
            $("#version").on("click", function () {
                clickVersionCount++;
                if (clickVersionCount === 5) {
                    $('#enableSupportAccess').prop("hidden", false);
                    clickVersionCount = 0;

                    setTimeout(function () {
                        $('#enableSupportAccess').prop("hidden", true);
                    }, 10000);
                }
            });
        }

        $("#enableSupportAccessToggle").on("load", function () {
            const queryString = window.location.search;
            if (queryString.toLowerCase().includes('showadmin')) {
                this.checked = true;
            } else {
                this.checked = false;
            }
        }).trigger("load");

        $("#enableSupportAccessToggle").off("click").on("click", function (e) {
            // Get the current URL without the hash
            let url = window.location.href.split('?')[0];
            let queryString = window.location.search;
            let supportAccessEnabled = this.checked; // Check if the checkbox is checked

            if (supportAccessEnabled) {
                if (!queryString.toLowerCase().includes('showadmin')) {
                    queryString = queryString ? queryString + '&ShowAdmin' : '?ShowAdmin';
                }
            } else {
                queryString = queryString.replace(/[\?&]ShowAdmin\b/i, '').replace(/^&/, '?');
            }

            window.history.replaceState({}, '', url + queryString);

            self.clearLoginOptionsError();
            self.loadAuthServices();
        });

        // We remove the template loader if there are errors already set
        if (self.loginTemplateContainsErrors()) {
            loginForm.hideLoaders();
            // We also clean the local storage since there are errors
            self.authCleanStorage();
        }

        // Handle auth
        if (BINamespace.authData) {
            self._loginResultHandler(BINamespace.parseJsonString(BINamespace.authData));
            return;
        }
        self.initSignalR();

        // Check for previously set RememberMe values in local storage and login
        let remembered = false;
        if (Modernizr.localstorage) {
            var storage = window.localStorage;
            if (storage.getItem("CompOAuthProviderId") &&
                storage.getItem("CompCentralPoint") &&
                storage.getItem("language") &&
                storage.getItem("forceMobileMode")) {
                remembered = true;
            }
            else if (storage.getItem("CompUsername") && storage.getItem("CompPassword") &&
                storage.getItem("CompCentralPoint") && storage.getItem("language") &&
                storage.getItem("forceMobileMode")) {
                remembered = true;
                loginForm.toggleSubmitButton(true);
                // show loading message
                $.ajax({
                    type: "POST",
                    url: loginForm.element.action,
                    data: {
                        Domain: storage.getItem("CompDomain") === "undefined" ? "" : storage.getItem("domain"),
                        UserName: storage.getItem("CompUsername"),
                        Password: storage.getItem("CompPassword"),
                        CentralPointHash: storage.getItem("CompCentralPoint"),
                        Encrypted: true,
                        RememberMe: true,
                        Language: storage.getItem("language"),
                        ForceMobileMode: storage.getItem("forceMobileMode"),
                        __RequestVerificationToken: $("input[name=__RequestVerificationToken]").val()
                    }
                }).done(self._loginResultHandler);
            }
        }
        if (!remembered || BINamespace.convertQueryStringToObject(window.location.href).CleanStorage === "True") {
            BINamespace.clearLocalStorageCredentials();
        }
    };

    return {
        initialize: self.initialize,
        onChangeCentralPoint: self.onChangeCentralPoint,
        onSetCentralPoint: self.onSetCentralPoint
    };
}.call({});

$(BINamespace.loginViewModel.initialize);

/**
 * Closes a dialog and removes it from the DOM.
 * This triggers the dialog's "close" event.
 * @param {MouseEvent} event
 */
function closeDialog(event) {
    const target = event.target;

    if (!(target instanceof NecIcon && target.classList.contains("closeDialog"))) {
        return;
    }

    const dialog = target.closest("dialog");
    dialog?.close();

    setTimeout(() => dialog?.remove());
}

document.body.addEventListener("click", closeDialog);

window.BINamespace.loginViewModel = BINamespace.loginViewModel;